/**
 * This component is the skeleton around the actual pages, and contains only
 * code that should be seen on all pages. (e.g. sidebar)
 */

import React, { Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet';
import { Switch } from 'react-router-dom';
import ReduxToastr from 'react-redux-toastr';
import * as Sentry from '@sentry/browser';

import styled from 'styled-components/macro';

import LoadingIndicator from 'ui/LoadingIndicator';
import Sidebar from 'components/Sidebar';
import Modal from 'components/Modal';
import PrivateRoute from 'components/PrivateRoute/PrivateRoute';

import { CSP_ENABLED, SENTRY_DSN, UI, URLS } from 'utils/constants';

const Credits = lazy(() => import('pages/Credits'));
const Flexboxes = lazy(() => import('pages/Flexboxes/Flexboxes'));
const NotFound = lazy(() => import('pages/NotFound'));
const PaymentOptions = lazy(() => import('pages/PaymentOptions'));
const Root = lazy(() => import('pages/Root/Root'));
const Sessions = lazy(() => import('pages/Sessions'));
const Unauthorised = lazy(() => import('pages/Unauthorised/Unauthorised'));
const Users = lazy(() => import('pages/Users'));
const Groups = lazy(() => import('pages/Groups/Groups'));
const GroupDetails = lazy(() => import('pages/GroupDetails/GroupDetails'));
const InstanceSettings = lazy(() =>
  import('pages/instanceSettings/InstanceSettings')
);
const SetupGuide = lazy(() => import('pages/SetupGuide/SetupGuide'));

Sentry.init({
  dsn: SENTRY_DSN,
  release: process.env.REACT_APP_BUILD_VERSION || undefined
});

const AppWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  margin: 0 auto;
`;

const RouteLoadingIndicatorWrapper = styled.div`
  position: absolute;
  inset: 0 0 0 ${UI.SIDEBAR_WIDTH + UI.PROJECT_BAR_WIDTH};
`;

const App = () => (
  <AppWrapper>
    <Helmet titleTemplate="CVF Admin - %s" defaultTitle="ClearView Flex admin">
      <meta
        name="description"
        content="Admin site for ClearView Flex from Sohonet"
      />
      {CSP_ENABLED && ( // disables CSP locally
        <meta
          httpEquiv="Content-Security-Policy"
          content="default-src 'self'; script-src 'self' https://js.stripe.com/v3/;"
        />
      )}
      <script src="https://js.stripe.com/v3/" />
    </Helmet>
    <Sidebar />
    <Suspense
      fallback={
        <RouteLoadingIndicatorWrapper>
          <LoadingIndicator cover />
        </RouteLoadingIndicatorWrapper>
      }
    >
      <Switch>
        <PrivateRoute exact path={URLS.ROOT} component={Root} />
        <PrivateRoute exact path="/projects/:projectId" component={Groups} />
        <PrivateRoute
          exact
          path="/projects/:projectId/groups/:groupId"
          component={GroupDetails}
        />
        <PrivateRoute
          exact
          path="/projects/:projectId/groups/:groupId/instances/:instanceId"
          component={InstanceSettings}
        />
        <PrivateRoute
          exact
          path="/projects/:projectId/users"
          component={Users}
        />
        <PrivateRoute
          exact
          path="/projects/:projectId/credits"
          component={Credits}
        />
        <PrivateRoute
          exact
          path="/projects/:projectId/sessions"
          component={Sessions}
        />
        <PrivateRoute
          exact
          path="/projects/:projectId/paymentOptions"
          component={PaymentOptions}
        />

        <PrivateRoute
          exact
          path="/projects/:projectId/instances"
          component={Flexboxes}
        />
        <PrivateRoute
          exact
          path="/projects/:projectId/instances/:instanceId"
          component={InstanceSettings}
        />
        <PrivateRoute
          path="/projects/:projectId/guide/:instanceId?"
          component={SetupGuide}
        />
        {/* Left as a Route for now. But a better error handling would be:
            - An error page in case something goes wrong during login
            - Show the unauthorised error without changing the URL */}
        <PrivateRoute path={URLS.UNAUTHORISED} component={Unauthorised} />
        <PrivateRoute path="" component={NotFound} />
      </Switch>
    </Suspense>
    <ReduxToastr
      timeOut={6000}
      getState={(state) => state.get('toastr')}
      newestOnTop={false}
      position="top-center"
      transitionIn="fadeIn"
      transitionOut="fadeOut"
      preventDuplicates
    />
    <Modal />
  </AppWrapper>
);

export default App;
